type ClientErrorPayload = {
  type?: string | null;
  level?: string | null;
  message: string;
  stack?: string | null;
  context?: Record<string, unknown>;
  url?: string;
  source?: string | null;
  lineno?: number | null;
  colno?: number | null;
  user_id?: number | string | null;
  user_agent?: string;
  time?: string;
}

type StackLocation = Pick<ClientErrorPayload, 'source' | 'lineno' | 'colno'>;

function getCurrentUserId(): ClientErrorPayload['user_id'] {
  try {
    return window.localStorage.getItem('client_logger_user_id');
  } catch {
    return null;
  }
}

function getStackLocation(stack?: string | null): StackLocation {
  if (!stack) return {};

  for (const line of stack.split('\n')) {
    const match = line.match(/\(?((?:webpack-internal|https?|file|\/|[A-Za-z]:).+):(\d+):(\d+)\)?$/);

    if (match) {
      return {
        source: match[1],
        lineno: Number(match[2]),
        colno: Number(match[3]),
      };
    }
  }

  return {};
}

function normalizePayload(payload: ClientErrorPayload): ClientErrorPayload {
  const stackLocation = getStackLocation(payload.stack);

  return {
    ...payload,
    source: payload.source ?? stackLocation.source ?? null,
    lineno: payload.lineno ?? stackLocation.lineno ?? null,
    colno: payload.colno ?? stackLocation.colno ?? null,
    user_id: payload.user_id ?? getCurrentUserId(),
    user_agent: payload.user_agent ?? navigator.userAgent,
    url: payload.url ?? window.location.href,
    time: payload.time ?? new Date().toISOString(),
  };
}

export function sendClientError(payload: ClientErrorPayload) {
  const normalizedPayload = normalizePayload(payload);
  const body = JSON.stringify(normalizedPayload);

  if (navigator.sendBeacon?.('/api/client-error', new Blob([body], { type: 'application/json' }))) {
    return;
  }

  void fetch('/api/client-error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  });
}

export function logClientError(error: unknown, context?: Record<string, unknown>) {
  const stack = error instanceof Error ? error.stack : null;

  sendClientError({
    type: typeof context?.type === 'string' ? context.type : 'client.error',
    message: error instanceof Error ? error.message : String(error),
    stack,
    context,
  });
}
